import React, {HTMLProps, useState} from 'react';
import axios from 'axios';
import Button from '@/components/ui/Button';
import {Tooltip} from '@/components/tooltips';
import {useStoreActions, useStoreState} from 'store';
import {Storyboard, StoryboardAccessType} from 'entities/storyboard';
import CellLoader from '@/components/loading/CellLoader';  
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import i18n from 'i18n';
import {ModalAlert} from '@/components/alerts';
import {buildStoryboardURL} from '@/utils/urlBuilder';

interface Props {
    storyboardId: number;
}

const StoryboardUserAccessTable = React.memo<Props>(({storyboardId}) => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [revokeConfirmOpen, setRevokeConfirmOpen] = useState<boolean>(false);
    const [usersToRevoke, setUsersToRevoke] = useState<number[]>([]);
    const [isRevoking, setIsRevoking] = useState<boolean>(false);

    const storyboards = useStoreState((state) => state.storyboards.items);
    const deleteStoryboardAccess = useStoreActions((actions) => actions.storyboards.deleteStoryboardAccess);
    const editStoryboardAccess = useStoreActions((actions) => actions.storyboards.editStoryboardAccess);

    const activeStoryboardIndex = storyboards.findIndex((sb: Storyboard) => sb.id === storyboardId);
    const activeStoryboard = activeStoryboardIndex !== -1 ? storyboards[activeStoryboardIndex] : null;
    const currentUserId = userData?.id;

    function IndeterminateCheckbox({
        indeterminate,
        className = '',
        ...rest
    }: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
        const ref = React.useRef<HTMLInputElement>(null!)

        React.useEffect(() => {
            if (typeof indeterminate === 'boolean') {
                ref.current.indeterminate = !rest.checked && indeterminate
            }
        }, [ref, indeterminate])

        return (
            <input
                type="checkbox"
                ref={ref}
                className={className + ' cursor-pointer'}
                {...rest}
            />
        )
    }

    const visibleUsers = activeStoryboard?.relatedUsers.filter((user) => user.id !== currentUserId) || [];

    const accessTypeI18nKeyByValue: Record<string, string> = {
        read: 'jt_access_view.text',
        write: 'jt_access_edit_and_delete.text',
    };

    // Selection helpers (aligned with StoryUserAccessTable)
    const allSelectableIds = visibleUsers.map((u) => u.id);
    const allSelected = selectedUsers.length > 0 && selectedUsers.length === allSelectableIds.length;
    const someSelected = selectedUsers.length > 0 && selectedUsers.length < allSelectableIds.length;

    const toggleAll = () => {
        if (allSelected) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(allSelectableIds);
        }
    };

    const toggleOne = (id: number) => {
        setSelectedUsers((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    // Early return if userData is not loaded yet - AFTER all hooks are defined
    if (loadingUserData || !userData) {
        return <CellLoader fullHeight={true} />;
    }

    if (activeStoryboardIndex === -1 || !activeStoryboard) {
        return null;
    }

    const deleteBulk = async () => {
        setUsersToRevoke(selectedUsers);
        setRevokeConfirmOpen(true);
    };

    const closeRevokeConfirm = (): void => {
        setUsersToRevoke([]);
        setRevokeConfirmOpen(false);
        setIsRevoking(false);
    };

    const confirmRevoke = async (): Promise<void> => {
        if (usersToRevoke.length === 0) {
            return;
        }

        setIsRevoking(true);
        try {
            const url = buildStoryboardURL('removeStoryboardPermBulk', storyboardId);
            await axios.post(url, {userIds: usersToRevoke});
            usersToRevoke.forEach((userId) => deleteStoryboardAccess([storyboardId, userId]));
            setSelectedUsers([]);
            closeRevokeConfirm();
        } catch (error) {
            setIsRevoking(false);
            throw error;
        }
    };

    const giveWriteAccess = async () => {
        const formData = {
            users: selectedUsers.map((value) => ({value})),
            accessType: {
                label: i18n.t('jt_access_edit_and_delete.text'),
                value: 'write',
            },
        };
        const url = buildStoryboardURL('shareStoryboard', storyboardId);
        const response = await axios.post(url, formData);
        editStoryboardAccess([storyboardId, response.data.relatedUsers]);
        setSelectedUsers([]);
    };

    const revokeAccessButtonDisabled = !selectedUsers.length;
    const giveWriteAccessButtonDisabled = !activeStoryboard.relatedUsers.filter(
        (user) => user.accessType !== 'write' && selectedUsers.includes(user.id),
    ).length;

    let revokeAccessButton = (
        <span className="float-right mt-2 ml-2">
            <Button
                variant="danger"
                disabled={revokeAccessButtonDisabled}
                onClick={deleteBulk}
                style={{pointerEvents: revokeAccessButtonDisabled ? 'none' : 'unset'}}
                data-cy="storyboard-user-access-table-danger-btn">
                {i18n.t('jt_revoke_access.text')}
            </Button>
        </span>
    );

    let giveWriteAccessButton = (
        <span className="float-right mt-2">
            <Button
                variant="success"
                disabled={giveWriteAccessButtonDisabled}
                onClick={giveWriteAccess}
                style={{pointerEvents: giveWriteAccessButtonDisabled ? 'none' : 'unset'}}
                data-cy="storyboard-user-access-table-success-btn">
                {i18n.t('jt_give_edit.text')}
            </Button>
        </span>
    );

    if (revokeAccessButtonDisabled) {
        revokeAccessButton = (
            <Tooltip
                content={i18n.t('jt_select_user.text')}
                placement="top"
            >
                <>
                    {revokeAccessButton}
                </>
            </Tooltip>
        );
    }

    if (giveWriteAccessButtonDisabled) {
        giveWriteAccessButton = (
            <Tooltip
                content={i18n.t('jt_select_less.text')}
                placement="top"
            >
                {giveWriteAccessButton}
            </Tooltip>
        );
    }

    const noDataIndication = i18n.t('jt_st_no_data_available.text');

    if (!loadingUserData) {
        return (
            <>
                <table className="table table-hover table-bordered g-table w-100">
                    <thead>
                        <tr>
                            <th className="border-right">
                                <IndeterminateCheckbox
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    onChange={toggleAll}
                                />
                            </th>
                            <th className="border-right">{i18n.t('th_username_email.text')}</th>
                            <th>{i18n.t('th_access_type.text')}</th>
                        </tr>
                    </thead>
                    {visibleUsers.length === 0 && (
                        <tbody>
                            <tr>
                                <td colSpan={3}>
                                    {noDataIndication}
                                </td>
                            </tr>
                        </tbody>
                    )}
                    <tbody>
                        {visibleUsers.map((user) => (
                            <tr key={user.id}>
                                <td>
                                    <input
                                        type="checkbox"
                                        className="cursor-pointer"
                                        checked={selectedUsers.includes(user.id)}
                                        onChange={() => toggleOne(user.id)}
                                    />
                                </td>
                                <td>{user.username} ({(user as any).email})</td>
                                <td>
                                    {accessTypeI18nKeyByValue[user.accessType]
                                        ? i18n.t(accessTypeI18nKeyByValue[user.accessType])
                                        : user.accessType}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {revokeAccessButton}

                {giveWriteAccessButton}

                <ModalAlert
                    isOpen={revokeConfirmOpen}
                    onClose={closeRevokeConfirm}
                    variant="warning"
                    title={i18n.t('jt_confirm_action.text')}
                    message={i18n.t('jt_st_revoke_permissions_confirm.text', {
                        count: usersToRevoke.length,
                        plural: usersToRevoke.length === 1 ? '' : 's'
                    })}
                    actions={[
                        {
                            label: i18n.t('jt_revoke.text'),
                            onClick: confirmRevoke,
                            variant: 'danger',
                            loading: isRevoking
                        }
                    ]}
                    dismissible={!isRevoking}
                />
            </>
        );
    } else {
        return (
            <CellLoader />
        );
    }
});

export default StoryboardUserAccessTable;
